// المخزن المحلي — فهرس المحفظة + ما لم يُزامَن بعد.
//
// ما يبقى على الجهاز: المستخدم والوجهات والأنواع وعدّادات استعمال مصغّرة
// وطابور الحركات التي لم تصل الخادم. أي تاريخ سابق لا يُخزَّن: يُطلب من
// الخادم لفترته حين تُفتح شاشة السجل أو التقرير، ويُنسى بعدها.
//
// لماذا: تخزين المتصفّح ٥ م.ب، أي ~١٥٧٠٠ حركة، وبعدها كان الحلّ إسقاط
// الأقدم صامتاً — وهي لا تعود لأن السحب تزايدي. بلا تاريخ محلي يزول
// السقف كلّه، ويبقى بلا اتصال ما طُلب: تسجيل حركة جديدة.
//
// كل كتابة تُطبَّق محلياً فوراً ثم تُوضع في طابور للدفع (تحديث متفائل).
// هذا ما يجعل التطبيق يبدو فورياً حتى على شبكة رديئة: المستخدم لا ينتظر
// رحلة ذهاب وإياب ليرى حركته، والطابور يتكفّل بالإيصال لاحقاً.
//
// أسماء الحقول مطابقة تماماً للخادم (destId, typeId, occurredAt, isFixed…)
// حتى تكون المزامنة نسخاً مباشراً بلا طبقة ترجمة تتعفّن مع الوقت.

import { debounce } from './util.js';

const KEY_PREFIX = 'familyWallet.v2';

export const CURRENCIES = {
  ILS: { sym: '₪',   name: 'شيكل'  },
  JOD: { sym: 'د.أ', name: 'دينار' },
  USD: { sym: '$',   name: 'دولار' },
  EUR: { sym: '€',   name: 'يورو'  },
  EGP: { sym: 'ج.م', name: 'جنيه'  },
  SAR: { sym: 'ر.س', name: 'ريال'  }
};

function emptyState(){
  return {
    user: null,
    household: null,
    members: [],
    dests: [],
    types: [],
    // مخزن جلسة لا أكثر: يحوي ما لم يُزامَن دائماً، وما جُلب لفترة معروضة مؤقتاً
    entries: [],
    window: null,       // { f, t } الفترة المحمّلة حالياً — null يعني لا شيء
    stats: null,        // عدّادات الاستعمال من الخادم (تُخزَّن)
    queue: [],          // عمليات لم تُدفع بعد
    lastSync: null,
    theme: 'system',
    // مدى «الصافي» في شريط التسجيل: 'all' أو 'month' — تفضيلُ جهازٍ لا بيانات
    netScope: 'all'
  };
}

export let state = emptyState();

/* --------------------------------------------------------------- التخزين */

// مفتاح لكل مستخدم: جهاز واحد قد يستخدمه الزوجان، وخلط بياناتهما
// في مفتاح واحد يعني رؤية أحدهما حركات الآخر بعد الخروج.
function storageKey(){
  return state.user ? `${KEY_PREFIX}.${state.user.id}` : `${KEY_PREFIX}.anon`;
}

const persist = debounce(() => saveNow(), 300);
export function save(){ persist(); }

/** الحركات التي لم يؤكّدها الخادم بعد — وحدها تستحق مكاناً على القرص. */
export function pendingEntries(){
  const queued = new Set(state.queue.map(op => op.id));
  return state.entries.filter(e => queued.has(e.id));
}

export function saveNow(){
  if (!state.user) return;
  try {
    localStorage.setItem(storageKey(), JSON.stringify({
      user: state.user, household: state.household, members: state.members,
      dests: state.dests, types: state.types,
      // ما جُلب للعرض لا يُحفظ: يُطلب ثانيةً عند الحاجة
      entries: pendingEntries(),
      stats: state.stats,
      queue: state.queue, lastSync: state.lastSync, theme: state.theme,
      netScope: state.netScope
    }));
    localStorage.setItem(`${KEY_PREFIX}.lastUser`, JSON.stringify(state.user));
  } catch (err){
    // لم يعد التاريخ محفوظاً، فالامتلاء يعني طابوراً ضخماً بلا اتصال طويل.
    // لا نُسقط شيئاً هنا: كل ما بقي غير مزامَن، وإسقاطه فقدان حقيقي.
    console.warn('تعذّر الحفظ محلياً:', err.name);
  }
}

export function loadFor(user){
  let raw = null;
  try { raw = localStorage.getItem(`${KEY_PREFIX}.${user.id}`); } catch { /* ignore */ }

  const fresh = emptyState();
  fresh.user = user;

  if (raw){
    try {
      const parsed = JSON.parse(raw);
      Object.assign(fresh, {
        household: parsed.household || null,
        members: parsed.members || [],
        dests: Array.isArray(parsed.dests) ? parsed.dests : [],
        types: Array.isArray(parsed.types) ? parsed.types : [],
        entries: Array.isArray(parsed.entries) ? parsed.entries : [],
        stats: parsed.stats || null,
        queue: Array.isArray(parsed.queue) ? parsed.queue : [],
        lastSync: parsed.lastSync || null,
        theme: parsed.theme || 'system',
        netScope: parsed.netScope === 'month' ? 'month' : 'all'
      });
      fresh.user = user;
    } catch (err){
      console.warn('بيانات محلية تالفة — سنعيد السحب من الخادم:', err.message);
    }
  }
  state = fresh;
  applyTheme();
  return state;
}

export function clearLocal(){
  const key = storageKey();
  const theme = state.theme;
  state = emptyState();
  state.theme = theme;
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(`${KEY_PREFIX}.lastUser`);
  } catch { /* ignore */ }
  applyTheme();
}

/** آخر مستخدم دخل على هذا الجهاز — لعرض الشاشة الصحيحة قبل وصول الشبكة. */
export function lastUser(){
  try {
    const raw = localStorage.getItem(`${KEY_PREFIX}.lastUser`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/* ---------------------------------------------------------------- المظهر */

export function setTheme(mode){ state.theme = mode; save(); applyTheme(); }

export function netScope(){ return state.netScope === 'month' ? 'month' : 'all'; }
export function toggleNetScope(){
  state.netScope = netScope() === 'all' ? 'month' : 'all';
  save();
  return state.netScope;
}
export function applyTheme(){
  const root = document.documentElement;
  if (state.theme === 'light' || state.theme === 'dark') root.setAttribute('data-theme', state.theme);
  else root.removeAttribute('data-theme');
}

/* ------------------------------------------------------------- الاستعلام */

export const destById = id => state.dests.find(d => d.id === id) || null;
export const typeById = id => state.types.find(t => t.id === id) || null;
export const destName = id => { const d = destById(id); return d ? d.name : 'محذوفة'; };
export const typeName = id => { const t = typeById(id); return t ? t.name : null; };

export const activeDests = () => state.dests.filter(d => !d.archived && !d.deleted);
export const activeTypes = () => state.types.filter(t => !t.archived && !t.deleted);
export const liveEntries = () => state.entries.filter(e => !e.deleted);

export const baseCur = () => (state.household && state.household.baseCurrency) || 'ILS';
export const baseSym = () => (CURRENCIES[baseCur()] || { sym: '₪' }).sym;

/** يحوّل مبلغ الحركة إلى العملة الأساسية بأسعار المحفظة. */
export function toBase(entry){
  const rates = (state.household && state.household.rates) || { ILS: 1 };
  const rate = Number(rates[entry.currency]) || 1;
  const base = Number(rates[baseCur()]) || 1;
  return entry.amount * rate / base;
}

/* ------------------------------------------------------ عدّادات الاستعمال */

/**
 * عدّادات صغيرة (وجهات × أنواع) تحلّ محلّ مسح تاريخ المحفظة.
 * منها يُشتق ترتيب البنود والوجهة التلقائية، فتبقى شاشة التسجيل ذكية
 * بلا اتصال دون أن نحمل التاريخ كلّه على الجهاز.
 */
export function emptyStats(){
  return { destsRecent: {}, typesRecent: {}, destTotals: {}, typeTotals: {},
           pairs: {}, monthTotal: 0, monthIncome: 0, allExpense: 0, allIncome: 0,
           untypedCount: 0, totalEntries: 0 };
}

export function setStats(incoming){
  state.stats = { ...emptyStats(), ...(incoming || {}) };
  save();
}

export function stats(){ return state.stats || emptyStats(); }

/** الصافي: الدخل ناقص المصروف — كلّ التاريخ أو هذا الشهر. */
export function netTotal(scope = 'all'){
  const s = stats();
  return scope === 'month'
    ? (s.monthIncome || 0) - (s.monthTotal || 0)
    : (s.allIncome || 0) - (s.allExpense || 0);
}

function inThisMonth(iso){
  const d = new Date(iso), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
}

/**
 * يحرّك العدّادات بعد كتابة محلية.
 * بدونه يسجّل المستخدم بلا اتصال فلا يتغيّر مجموع الشهر أمامه ولا يتقدّم
 * البند الذي استعمله للتوّ — وكأن التطبيق لم يسمع.
 */
export function bumpStats(entry, sign = 1){
  if (!state.stats || !entry) return;
  const s = state.stats;
  const add = (bag, key, n) => {
    if (!key) return;
    const next = (bag[key] || 0) + n;
    if (next > 0) bag[key] = next; else delete bag[key];
  };

  add(s.destsRecent, entry.destId, sign);
  add(s.destTotals, entry.destId, sign);
  if (entry.typeId) add(s.typeTotals, entry.typeId, sign);
  if (entry.kind !== 'in'){
    if (entry.typeId){
      add(s.typesRecent, entry.typeId, sign);
      const row = s.pairs[entry.destId] || (s.pairs[entry.destId] = {});
      add(row, entry.typeId, sign);
      if (!Object.keys(row).length) delete s.pairs[entry.destId];
    } else {
      s.untypedCount = Math.max(0, (s.untypedCount || 0) + sign);
    }
    if (inThisMonth(entry.occurredAt)){
      s.monthTotal = Math.max(0, (s.monthTotal || 0) + sign * toBase(entry));
    }
  }

  // مجاميعُ «الصافي» — تتحرّك محلّيّاً مع كلِّ كتابة، فالرقم ينزل أو يرتفع
  // لحظةَ الضغط على «تم» ولو بلا اتصال
  const amount = sign * toBase(entry);
  if (entry.kind === 'in'){
    s.allIncome = Math.max(0, (s.allIncome || 0) + amount);
    if (inThisMonth(entry.occurredAt)) s.monthIncome = Math.max(0, (s.monthIncome || 0) + amount);
  } else {
    s.allExpense = Math.max(0, (s.allExpense || 0) + amount);
  }

  s.totalEntries = Math.max(0, (s.totalEntries || 0) + sign);
}

/* ----------------------------------------------------- الكتابة عبر الطابور */

export function uuid(){
  if (crypto.randomUUID) return crypto.randomUUID();
  // احتياطي: randomUUID غير متاح في سياق غير آمن (http على شبكة محلية)
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
}

let opCounter = 0;
function enqueue(op){
  op.opId = `${Date.now().toString(36)}-${(opCounter++).toString(36)}`;
  // عملية أحدث على نفس العنصر تُلغي أقدم منها: لا فائدة من دفع ثلاث نسخ
  // من حركة عُدّلت ثلاث مرات بلا اتصال.
  const i = state.queue.findIndex(q => q.id === op.id && q.type === op.type);
  if (i >= 0) state.queue[i] = op; else state.queue.push(op);
  save();
}

export function pendingCount(){ return state.queue.length; }

export function addEntry({ amount, destId, typeId = null, currency, kind = 'out', occurredAt, note = '' }){
  const entry = {
    id: uuid(),
    destId,
    typeId: typeId || null,
    amount: Math.round(Number(amount) * 100) / 100,
    currency: currency || baseCur(),
    kind: kind === 'in' ? 'in' : 'out',
    occurredAt: (occurredAt ? new Date(occurredAt) : new Date()).toISOString(),
    note: note || '',
    userId: state.user ? state.user.id : null,
    deleted: false,
    updatedAt: new Date().toISOString()
  };
  state.entries.push(entry);
  bumpStats(entry, +1);
  enqueue({ type: 'entry.upsert', ...serializeEntry(entry) });
  return entry;
}

export function updateEntry(id, patch){
  const e = state.entries.find(x => x.id === id);
  if (!e) return null;
  bumpStats(e, -1);                    // نسحب القيم القديمة قبل تغييرها
  Object.assign(e, patch);
  if (patch.amount != null) e.amount = Math.round(Number(patch.amount) * 100) / 100;
  if (patch.occurredAt) e.occurredAt = new Date(patch.occurredAt).toISOString();
  e.updatedAt = new Date().toISOString();
  bumpStats(e, +1);
  enqueue({ type: 'entry.upsert', ...serializeEntry(e) });
  return e;
}

export function removeEntry(id){
  const e = state.entries.find(x => x.id === id);
  if (!e) return null;
  const snapshot = { ...e };
  bumpStats(e, -1);
  e.deleted = true;
  e.updatedAt = new Date().toISOString();
  enqueue({ type: 'entry.delete', id });
  return snapshot;
}

export function removeEntries(ids){
  return ids.map(id => removeEntry(id)).filter(Boolean);
}

/** التراجع: نعيد إحياء الحركة بنفس معرّفها فتُطابَق على الخادم بلا تكرار. */
export function restoreEntries(list){
  list.forEach(snap => {
    const existing = state.entries.find(x => x.id === snap.id);
    if (existing) Object.assign(existing, snap, { deleted: false, updatedAt: new Date().toISOString() });
    else state.entries.push({ ...snap, deleted: false, updatedAt: new Date().toISOString() });
    const e = state.entries.find(x => x.id === snap.id);
    bumpStats(e, +1);
    enqueue({ type: 'entry.upsert', ...serializeEntry(e) });
  });
}

function serializeEntry(e){
  return {
    id: e.id, destId: e.destId, typeId: e.typeId, amount: e.amount,
    currency: e.currency, kind: e.kind, occurredAt: e.occurredAt,
    note: e.note || null, updatedAt: e.updatedAt
  };
}

/* ------------------------------------------------- الوجهات والأنواع */

export function addDest(name){
  const clean = String(name || '').trim();
  if (!clean) return null;
  const dup = state.dests.find(d => d.name.toLowerCase() === clean.toLowerCase());
  if (dup){
    if (dup.archived || dup.deleted) restoreDest(dup.id);
    return dup;
  }
  const d = { id: uuid(), name: clean, sortOrder: state.dests.length,
              archived: false, deleted: false, updatedAt: new Date().toISOString() };
  state.dests.push(d);
  enqueue({ type: 'dest.upsert', id: d.id, name: d.name, sortOrder: d.sortOrder, archived: false });
  return d;
}

export function addType(name, isFixed = false){
  const clean = String(name || '').trim();
  if (!clean) return null;
  const dup = state.types.find(t => t.name.toLowerCase() === clean.toLowerCase());
  if (dup){
    if (dup.archived || dup.deleted) restoreType(dup.id);
    return dup;
  }
  const t = { id: uuid(), name: clean, isFixed: !!isFixed, sortOrder: state.types.length,
              archived: false, deleted: false, updatedAt: new Date().toISOString() };
  state.types.push(t);
  enqueue({ type: 'type.upsert', id: t.id, name: t.name, isFixed: t.isFixed,
            sortOrder: t.sortOrder, archived: false });
  return t;
}

export function renameDest(id, name){
  const d = destById(id); const clean = String(name || '').trim();
  if (!d || !clean) return;
  d.name = clean; d.updatedAt = new Date().toISOString();
  enqueue({ type: 'dest.upsert', id: d.id, name: d.name, sortOrder: d.sortOrder || 0, archived: !!d.archived });
}

export function renameType(id, name){
  const t = typeById(id); const clean = String(name || '').trim();
  if (!t || !clean) return;
  t.name = clean; t.updatedAt = new Date().toISOString();
  enqueue({ type: 'type.upsert', id: t.id, name: t.name, isFixed: !!t.isFixed,
            sortOrder: t.sortOrder || 0, archived: !!t.archived });
}

export function toggleFixed(id){
  const t = typeById(id);
  if (!t) return;
  t.isFixed = !t.isFixed; t.updatedAt = new Date().toISOString();
  enqueue({ type: 'type.upsert', id: t.id, name: t.name, isFixed: t.isFixed,
            sortOrder: t.sortOrder || 0, archived: !!t.archived });
}

/** عدد حركات وجهة أو نوع — من العدّادات، فالتاريخ لم يعد على الجهاز. */
export function usageCount(kind, id){
  const s = stats();
  const bag = kind === 'dest' ? s.destTotals : s.typeTotals;
  return (bag && bag[id]) || 0;
}

/**
 * الحذف أرشفة دائماً: الحركات التاريخية تشير إلى الوجهة،
 * وحذفها الصلب يجعل تقارير الأشهر الماضية بلا معنى.
 */
export function removeDest(id){
  const d = destById(id);
  if (!d) return 0;
  const used = usageCount('dest', id);
  d.archived = true; d.deleted = true; d.updatedAt = new Date().toISOString();
  enqueue({ type: 'dest.delete', id });
  return used;
}

export function removeType(id){
  const t = typeById(id);
  if (!t) return 0;
  const used = usageCount('type', id);
  t.archived = true; t.deleted = true; t.updatedAt = new Date().toISOString();
  enqueue({ type: 'type.delete', id });
  return used;
}

export function restoreDest(id){
  const d = destById(id);
  if (!d) return;
  d.archived = false; d.deleted = false; d.updatedAt = new Date().toISOString();
  enqueue({ type: 'dest.upsert', id: d.id, name: d.name, sortOrder: d.sortOrder || 0, archived: false });
}

export function restoreType(id){
  const t = typeById(id);
  if (!t) return;
  t.archived = false; t.deleted = false; t.updatedAt = new Date().toISOString();
  enqueue({ type: 'type.upsert', id: t.id, name: t.name, isFixed: !!t.isFixed,
            sortOrder: t.sortOrder || 0, archived: false });
}

/* -------------------------------------------------- دمج نتيجة السحب */

/**
 * يدمج ما وصل من الخادم في النسخة المحلية.
 * العنصر الذي ما زال في الطابور لا يُستبدل: تعديل المستخدم المحلي أحدث
 * مما سحبناه، واستبداله يجعل تعديله يختفي أمام عينيه ثم يعود.
 */
export function mergeFromServer(payload){
  const queued = new Set(state.queue.map(op => op.id));

  if (payload.household) state.household = payload.household;
  if (payload.members) state.members = payload.members;

  mergeList(state.dests, payload.dests, queued);
  mergeList(state.types, payload.types, queued);
  mergeList(state.entries, payload.entries, queued);

  state.lastSync = payload.since || payload.serverTime;
  save();
}

function mergeList(local, incoming, queued){
  if (!Array.isArray(incoming)) return;
  const index = new Map(local.map((x, i) => [x.id, i]));
  for (const row of incoming){
    if (queued.has(row.id)) continue;   // تعديلنا المحلي أحدث
    const i = index.get(row.id);
    if (i === undefined){ local.push(row); index.set(row.id, local.length - 1); }
    else local[i] = row;
  }
}

/**
 * يضع نتيجة جلب فترة في مخزن الجلسة.
 * ما لم يُزامَن يبقى دائماً في المقدّمة: قد يكون خارج الفترة المطلوبة،
 * وإسقاطه يعني اختفاء حركة سجّلها المستخدم للتوّ بلا اتصال.
 */
export function setWindow(f, t, incoming, hasMore, untyped = false){
  const pending = pendingEntries();
  const seen = new Set(pending.map(e => e.id));
  state.entries = pending.concat((incoming || []).filter(e => !seen.has(e.id)));
  state.window = { f: +f, t: +t, hasMore: !!hasMore, untyped: !!untyped };
}

/** يُنسي الفترة المعروضة — يُستدعى عند الخروج أو تبديل المستخدم. */
export function clearWindow(){
  state.entries = pendingEntries();
  state.window = null;
}

/** يُستدعى بعد نجاح الدفع: تُحذف العمليات التي أكّدها الخادم. */
export function clearQueued(opIds){
  const done = new Set(opIds);
  state.queue = state.queue.filter(op => !done.has(op.opId));
  save();
}
